import React from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AnalyticsTab = ({ 
  loading, 
  userData,
  getLevelInfo,
  getNextLevelUnlock,
  getMindStreamColor
}) => {
  if (loading || !userData || !userData.mindstreams) {
    return (
      <div className="emit-loading">
        <div className="emit-loading-dot"></div>
        <div className="emit-loading-dot"></div>
        <div className="emit-loading-dot"></div>
      </div>
    );
  }

  const mindstreamData = userData.mindstreams.map(stream => {
    let fill = '#667eea'; // Power - blue
    if (stream.type === 'flow') fill = '#ef4444'; // Flow - red
    if (stream.type === 'clarity') fill = '#10b981'; // Clarity - green
    
    return {
      name: stream.name,
      sessions: stream.sessions || 0,
      minutes: stream.time_minutes || 0,
      type: stream.type,
      fill: fill
    };
  });

  const timeDistribution = [
    { name: 'Power', value: userData.mindstreams[0]?.time_minutes || 0, fill: '#667eea' },
    { name: 'Flow', value: userData.mindstreams[1]?.time_minutes || 0, fill: '#ef4444' },
    { name: 'Clarity', value: userData.mindstreams[2]?.time_minutes || 0, fill: '#10b981' }
  ];

  const totalMinutes = userData.mindstreams.reduce((sum, s) => sum + (s.time_minutes || 0), 0);
  const totalHours = (totalMinutes / 60).toFixed(1);
  const consciousnessPoints = userData.profile?.consciousness_points || 0;

  return (
    <div className="emit-chat-container" style={{ maxWidth: '1200px' }}>
      {/* Link to Journey Dashboard */}
      <div className="emit-card" style={{ 
        marginBottom: '24px', 
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
        border: '1px solid rgba(102, 126, 234, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', color: '#4A5568', fontSize: '1rem', fontWeight: '600' }}>
              📊 Full Progress Dashboard
            </h3>
            <p style={{ margin: '0', fontSize: '0.875rem', color: '#4A5568' }}>
              View milestones, Journey points, streaks & transformation
            </p>
          </div>
          <a 
            href="/journey" 
            style={{
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}
          >
            My Journey →
          </a>
        </div>
      </div>

      {/* Consciousness Level Display */}
      <div className="emit-card" style={{ marginBottom: '24px' }}>
        {(() => {
          const points = consciousnessPoints;
          const levelInfo = getLevelInfo(points);
          const { currentLevel, nextLevel, percentComplete, pointsToNext, isMaxLevel } = levelInfo;

          return (
            <>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '0.75rem', color: '#4A5568', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Consciousness Points
                </div>
                <h3 style={{ marginBottom: '8px', color: currentLevel.color, fontWeight: 'bold', fontSize: '1.5rem' }}>
                  Level {currentLevel.level}: {currentLevel.name}
                </h3>
                <div style={{
                  display: 'inline-block',
                  padding: '6px 16px',
                  background: 'rgba(108, 117, 125, 0.1)',
                  borderRadius: '20px',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '0.875rem' }}>🎖️ {currentLevel.badge}</span>
                </div>
                <p style={{ fontSize: '1.25rem', color: currentLevel.color, margin: '0', fontWeight: '600' }}>
                  {points.toLocaleString()} pts
                </p>
              </div>

              {/* Progress Bar */}
              {!isMaxLevel && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <small style={{ color: '#4A5568' }}>Progress to Level {nextLevel.level}</small>
                    <small style={{ color: '#4A5568', fontWeight: 'bold' }}>{percentComplete}%</small>
                  </div>
                  <div style={{
                    height: '24px',
                    background: '#E2E8F0',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <div
                      style={{
                        width: `${percentComplete}%`,
                        height: '100%',
                        background: `linear-gradient(90deg, ${currentLevel.color}, ${currentLevel.color}dd)`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        transition: 'width 0.5s ease'
                      }}
                    >
                      {percentComplete > 10 ? `${percentComplete}%` : ''}
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Row */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '12px',
                textAlign: 'center'
              }}>
                <div style={{ padding: '16px', background: '#F7FAFC', borderRadius: '12px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0d6efd', marginBottom: '4px' }}>
                    {pointsToNext.toLocaleString()}
                  </div>
                  <small style={{ color: '#4A5568' }}>Points to Next Level</small>
                </div>
                <div style={{ padding: '16px', background: '#F7FAFC', borderRadius: '12px' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#198754', marginBottom: '4px' }}>
                    ~{Math.ceil(pointsToNext / 28)}
                  </div>
                  <small style={{ color: '#4A5568' }}>Practices Needed</small>
                </div>
              </div>

              {/* Next Level Preview */}
              {!isMaxLevel && (
                <div style={{
                  marginTop: '16px',
                  padding: '14px',
                  background: 'rgba(13, 110, 253, 0.05)',
                  borderRadius: '10px',
                  borderLeft: '3px solid #0d6efd'
                }}>
                  <div style={{ fontSize: '0.875rem' }}>
                    <strong style={{ color: '#0d6efd' }}>Next:</strong> Level {nextLevel.level} - {nextLevel.name}
                    <span style={{ color: '#4A5568', marginLeft: '8px' }}>({getNextLevelUnlock(nextLevel.level)})</span>
                  </div>
                </div>
              )}

              {/* Max Level Message */}
              {isMaxLevel && (
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  background: 'rgba(255, 193, 7, 0.1)',
                  borderRadius: '12px',
                  textAlign: 'center'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>👑</span>
                  <p style={{ margin: '8px 0 0', fontSize: '0.875rem', color: '#4A5568' }}>
                    Divine Stasis Achieved - Continue practicing to deepen mastery
                  </p>
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Charts Side by Side */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
        <div className="emit-card">
          <h5 style={{ marginBottom: '16px', color: 'var(--deep-blue)' }}>📊 Sessions by MindStream</h5>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={mindstreamData} barSize={50}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(74, 144, 226, 0.2)" />
              <XAxis dataKey="name" stroke="#718096" fontSize={12} />
              <YAxis stroke="#718096" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  background: 'rgba(255, 255, 255, 0.95)', 
                  borderRadius: '8px', 
                  border: 'none', 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                }} 
              />
              <Bar dataKey="sessions" name="Sessions" radius={[6, 6, 0, 0]}>
                {mindstreamData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="emit-card">
          <h5 style={{ marginBottom: '16px', color: 'var(--deep-blue)' }}>⏱️ Time Distribution</h5>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={timeDistribution.filter(d => d.value > 0)}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => value > 0 ? `${name}: ${value}m` : ''}
                outerRadius={70}
                dataKey="value"
              >
                {timeDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  background: 'rgba(255, 255, 255, 0.95)', 
                  borderRadius: '8px', 
                  border: 'none', 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                }} 
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <small style={{ color: '#4A5568' }}>Total: {totalHours} hours ({totalMinutes} min)</small>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div style={{ 
        marginTop: '24px', 
        padding: '12px 16px', 
        background: 'rgba(102, 126, 234, 0.05)', 
        borderRadius: '8px',
        fontSize: '0.8rem',
        color: '#4A5568',
        textAlign: 'center'
      }}>
        💡 <strong>Consciousness Points</strong> track spiritual growth (28 pts/practice) • <strong>Journey Points</strong> in My Journey track overall engagement
      </div>
    </div>
  );
};

export default AnalyticsTab;
